
/* injects from baggage-loader */
require('./style.styl');

import React, {Component, Fragment, isValidElement} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import throttle from 'lodash/throttle'
import groupBy from 'lodash/groupBy'
import without from 'lodash/without'
import chunk from 'lodash/chunk'

import Checkbox from 'components/Checkbox'
import Icon from 'components/Icon'
import Button from 'components/Button'

class MultiSelect extends Component {
  static propTypes = {
    options: PropTypes.array,
    disabled: PropTypes.bool,
    values: PropTypes.array,
    onChange: PropTypes.func,
    addIcon: PropTypes.string,
    groupKey: PropTypes.string,
    groupTitleKey: PropTypes.string,
    groupCount: PropTypes.number,
    placeholder: PropTypes.string,
    contentClassName: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.state = {
      selectorRef: null,
      positionModification: 0,
      opened: false,
      selectedValues: null
    }

    this.onThrottledWindowResize = throttle(this.onWindowResize, 100)
  }

  componentDidMount() {
    window.addEventListener('resize', this.onThrottledWindowResize)
    this.onWindowResize()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.opened === prevState.opened) return true

    if (this.state.opened) {
      document.addEventListener('click', this.onHideSelector, false)
      this.onWindowScroll()
      window.addEventListener('scroll', this.onWindowScroll, false)
    } else {
      document.removeEventListener('click', this.onHideSelector, false)
      window.removeEventListener('scroll', this.onWindowScroll, false)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onHideSelector)
    window.removeEventListener('resize', this.onThrottledWindowResize)
  }

  render() {
    const {values, disabled} = this.props
    const classNames = classnames('multiselect', {
      'is-empty': !values.length,
      'is-disabled': disabled
    })

    return (
      <div className={classNames}>
        {this.renderAddButton()}
      </div>
    )
  }

  renderAddButton() {
    const {
      opened,
      positionModification
    } = this.state

    if (opened) {
      return (
        <div
          className='multiselect_selector'
        >
          {this.renderOpenedButton()}
          <div
            className='multiselect_selector_body'
            ref={this.onSelectorRef}
            style={{
              left: positionModification
            }}
          >
            {this.renderSelectorBody()}
          </div>
        </div>
      )
    }

    return this.renderClosedButton()
  }

  renderOpenedButton() {
    const {addIcon} = this.props

    const classNames = classnames('multiselect_selector_tip', {
      'with-custom-icon': !!addIcon
    })

    return (
      <div className={classNames}>
        <Icon name={addIcon || 'new-arrow-up'} size='13px' />
      </div>
    )
  }

  renderClosedButton() {
    const {opened} = this.state
    const {placeholder, values, addIcon, disabled} = this.props
    const isEmpty = !values || !values.length

    return (
      <Button
        disabled={disabled}
        className='multiselect_add-button'
        color={addIcon ? 'transparent' : 'blue'}
        border={false}
        onClick={opened ? this.onHideSelector : this.onShowSelector}
      >
        <Icon name={addIcon || 'new-plus'} size='13px' />
        {isEmpty ? <span className='multiselect_add-button_empty'>{placeholder}</span> : null}
      </Button>
    )
  }

  renderSelectorBody() {
    const {contentClassName, groupKey} = this.props
    const {
      windowHeight,
      windowScroll,
      selectorTopOffset,
      selectorLeft,
      selectorWidth,
      positionModification
    } = this.state
    const isSticky = selectorTopOffset >= windowHeight + windowScroll
    const contentClassNames = classnames('multiselect_selector_content', contentClassName, {
      'is-narrow': !groupKey,
      'with-sticky': isSticky
    })
    const classNames = classnames('multiselect_selector_apply', {
      'is-narrow': !groupKey,
      'is-sticky': isSticky
    })
    const styles = isSticky ? {
      left: selectorLeft + positionModification,
      width: selectorWidth
    } : null

    return (
      <Fragment>
        <div className={contentClassNames}>
          {this.renderSelectorItems()}
        </div>
        <div className={classNames} style={styles}>
          <Button
            border={false}
            color='transparent'
            className='multiselect_selector_apply_reset'
            onClick={this.onResetValues}
          >
            Reset
          </Button>
          <Button color='blue' className='multiselect_selector_apply_button' onClick={this.onApplyValues}>
            Apply
          </Button>
        </div>
      </Fragment>
    )
  }

  renderSelectorItems() {
    const {options, groupCount, groupKey} = this.props
    const {selectedValues} = this.state
    const checkSelected = item => {
      if (!item.groupMain) {
        return {
          ...item,
          selected: selectedValues.includes(item.value)
        }
      }

      return {
        ...item,
        selected: !!item.variants.find(v => selectedValues.includes(v.value))
      }
    }
    const items = options.reduce((acc, item) => {
      const newItem = checkSelected(item)
      if (!item.variants) return acc.concat(newItem)

      return acc.concat({
        ...newItem,
        variants: item.variants.map(checkSelected)
      })
    }, [])
    const groups = this.getGroups(items)

    if (groups) {
      const classNames = classnames('multiselect_selector_groups', {
        [`with-${groups.length}-groups`]: !!groupCount && !groupKey
      })
      return (
        <div className={classNames}>
          {groups.map(({title, items}, index) => (
            <div className='multiselect_selector_group' key={index}>
              {title ? <h3>{title}</h3> : null}
              {items.map((item, index) => {
                if (item.variants) {
                  const classNames = classnames('multiselect_selector_group_variants', {
                    'is-selected': item.selected
                  })
                  return (
                    <div className={classNames} key={index}>
                      {this.renderCheckbox(item, index)}
                      {item.variants.map(this.renderCheckbox)}
                    </div>
                  )
                } else {
                  return this.renderCheckbox(item, index)
                }
              })}
            </div>
          ))}
        </div>
      )
    } else {
      return (
        <Fragment>{items.map(this.renderCheckbox)}</Fragment>
      )
    }
  }

  renderCheckbox = (item, index) => {
    const {value, label, selected, isVariant, groupMain, disabled, icon, variants} = item
    const classNames = classnames('multiselect_selector_checkbox', {
      'multiselect_selector_group_variant': isVariant,
      'is-selected': selected
    })
    const partialyChecked = variants && !variants.every(i => i.selected) && variants.some(i => i.selected)

    return (
      <Checkbox
        className={classNames}
        key={index}
        disabled={disabled}
        id={value.toString()}
        checked={selected}
        partialyChecked={partialyChecked}
        onChange={this.onItemChange(value, groupMain)}
        align={icon ? 'right' : 'left'}
        noIcon={groupMain}
      >
        {this.renderCheckboxIcon(item)}{label}
      </Checkbox>
    )
  }

  renderCheckboxIcon(item) {
    const {icon} = item

    if (!icon) return null

    if (isValidElement(icon)) return icon

    return <Icon className='multiselect_selector_checkbox_icon' name={icon} />
  }

  onApplyValues = evt => {
    const {selectedValues} = this.state
    const {onChange} = this.props

    onChange && onChange(selectedValues)
    this.onHideSelector(evt, true)
  }

  onItemChange = (value, groupMain) => () => {
    const {selectedValues} = this.state
    const {options} = this.props
    const isExisting = selectedValues.includes(value)

    if (groupMain) {
      const {variants: vars} = options.find(opt => opt.value === value)
      const variants = vars.map(v => v.value)

      this.setState({
        selectedValues: isExisting
          ? without(selectedValues, ...variants)
          : selectedValues.concat(without(variants, selectedValues))
      })
    } else {
      this.setState({
        selectedValues: isExisting ? without(selectedValues, value) : selectedValues.concat(value)
      })
    }
  }

  onWindowResize = () => {
    this.setState({
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    })
  }

  onWindowScroll = () => {
    this.setState({
      windowScroll: window.pageYOffset
    })
  }

  onSelectorRef = el => {
    const {windowWidth, selectorRef} = this.state

    if (el && !selectorRef) {
      const {width: selectorWidth, right: selectorRight, left: selectorLeft, top, height} = el.getBoundingClientRect()
      const offset = windowWidth - (selectorRight + 30)
      const selectorTopOffset = top + window.pageYOffset + height
      this.setState({
        positionModification: offset < 0 ? offset : 0,
        selectorRef: el,
        selectorTopOffset,
        selectorLeft,
        selectorWidth
      })
    }
  }

  onResetValues = evt => {
    const {onChange} = this.props
    onChange && onChange([])
    this.onHideSelector(evt, true)
  }

  onShowSelector = () => {
    const {values, disabled} = this.props

    if (disabled) return null

    this.setState({
      opened: true,
      selectedValues: values.concat()
    })
  }

  onHideSelector = (evt, force) => {
    const {selectorRef} = this.state

    if (!force && selectorRef && selectorRef.contains(evt.target)) return

    this.setState({
      opened: false,
      selectedValues: null,
      positionModification: 0,
      selectorTopOffset: null,
      selectorRef: null
    })
  }

  getGroups(items) {
    const {groupKey, groupCount, groupTitleKey} = this.props

    if (!groupKey && !groupCount) return null

    if (groupKey) {
      const groupedItems = groupBy(items, groupKey)

      return Object.keys(groupedItems).map(group => {
        return {
          title: groupTitleKey && groupedItems[group][0][groupTitleKey],
          items: groupedItems[group]
        }
      })
    }

    return chunk(items, groupCount).map(items => ({items}))
  }
}

export default MultiSelect



// WEBPACK FOOTER //
// ./client/components/Multiselect/index.js